import React, { useState, useEffect } from 'react';
import { Icons, useTheme } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { SetupButton } from './SsoPromoContents/SetupButton';
import { SsoBannerSvg } from './SsoPromoContents/SsoPromosSvg';
import {
  BannerContainer,
  StyledCloseButton,
  BannerContent,
  SvgContainer,
  BannerText,
  PrimaryText,
  SecondaryText,
} from './components';
import { useSsoPromoLogic } from './ssoUtils';

type SsoPromoBannerProps = {
  ssoPromoLogic?: ReturnType<typeof useSsoPromoLogic>;
  screenSize?: ReturnType<typeof useScreenSize>;
  cyDashboard?: boolean;
  hasCustomStyle?: boolean;
};

export const SsoPromoBanner = ({
  ssoPromoLogic = useSsoPromoLogic(),
  screenSize = useScreenSize(),
  cyDashboard = false,
  hasCustomStyle = false,
}: SsoPromoBannerProps) => {
  const { brandName, showApple, isOnSsoTest } = ssoPromoLogic;
  const { isMobile, isMedium } = screenSize;
  const theme = useTheme();

  const secText = showApple
    ? 'Enable Google & Apple Sign-In!'
    : 'Enable Google Sign-In!';

  const [isVisible, setIsVisible] = useState(() => {
    const saved = localStorage.getItem('ssoBannerVisible');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const isDashboardPage =
    location.pathname.includes('/dashboard') || cyDashboard;

  const shouldRender =
    isOnSsoTest && isDashboardPage && !isMobile && !isMedium && isVisible;

  useEffect(() => {
    const appContainer = document.getElementById('app-container');
    const headerContainer = document.getElementById('styled-header');
    if (appContainer) {
      const marginTop = hasCustomStyle
        ? shouldRender
          ? theme.spacing(10)
          : 'auto'
        : shouldRender
        ? theme.spacing(16)
        : theme.spacing(7);

      appContainer.style.marginTop = marginTop;
    }
    if (hasCustomStyle && headerContainer) {
      headerContainer.style.minHeight = shouldRender ? '125px' : 'auto';
    }
  }, [shouldRender]);

  useEffect(() => {
    localStorage.setItem('ssoBannerVisible', JSON.stringify(isVisible));
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!shouldRender) return null;

  return (
    <BannerContainer data-testid="sso-promo-banner">
      <BannerContent>
        <SvgContainer>
          <SsoBannerSvg
            brandName={brandName}
            showApple={showApple}
            style={{ height: '100%', width: 'auto' }}
          />
        </SvgContainer>
        <BannerText>
          <PrimaryText id="first-text">Simplify Your Login:</PrimaryText>{' '}
          <SecondaryText id="sec-text" data-testid="secondary-text">
            {secText}
          </SecondaryText>
        </BannerText>
        <SetupButton handleClose={handleClose} data-testid="setup-button" />
        <StyledCloseButton onClick={handleClose} data-testid="close-button">
          <Icons.Close fontSize="small" />
        </StyledCloseButton>
      </BannerContent>
    </BannerContainer>
  );
};
